import { UI, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import React, { useEffect, useState } from 'react';

import { InputMoney } from '../../../../../../components/InputMoney';
import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { fundsWithdrawSimulateTax } from '../../../../../../store/redemptionSolicitation/selector';
import { FundWithdrawAssetNormalized } from '../../../../../../store/redemptionSolicitation/types';
import { CardTitle } from '../../../../components/CardTitle';
import { CustomItem } from '../../../../components/CustomItem';
import { FundRules } from '../../../../components/FundRules';
import { HighlightedInfos } from '../../../../components/HighlightedInfos';
import { InfoItem } from '../../../../components/InfoItem';
import { ProductCard } from '../../../../components/ProductCard';
import { useRedemptionController } from '../../../../hooks/useRedemptionController';

export interface ValueSelectionStepProps {
  fundAssetNormalized: FundWithdrawAssetNormalized;
  handleNextStep: (
    totalValueChecked: boolean,
    grossValueSelected: number,
    liquidityValueSelected: number
  ) => void;
}

export const ValueSelectionStep: React.FC<ValueSelectionStepProps> = ({
  handleNextStep,
  fundAssetNormalized,
}) => {
  const { showToastFromError } = useToastCreator('redemption-fund-tax-toast-mc');
  const [totalValueChecked, setTotalValueChecked] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(true);

  const fundInfo = fundAssetNormalized.fund;

  const {
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    control,
    hasError,
    isFocused,
    valueAppliedFormatted,
    valueApplied,
  } = useRedemptionController(fundInfo.minimumTransaction, fundInfo.grossTotal);

  const valueToSimulate = totalValueChecked ? fundInfo.grossTotal : valueApplied;

  const { data, loading } = useRecoilQuery(
    fundsWithdrawSimulateTax({
      fundId: fundInfo.fundId,
      portfolioId: fundAssetNormalized.portfolioId,
      value: valueToSimulate,
    })
  );

  const handleToggleTotalValue = () => {
    const newValue = !totalValueChecked;

    if (newValue) {
      setAgreementChecked(true);
    }

    setTotalValueChecked(newValue);
  };

  const handleButtonPress = () => {
    handleNextStep(totalValueChecked, valueToSimulate, data?.liquidityValue || 0);
  };

  useEffect(() => {
    if (!loading && data && !data.isBalanceAvailable) {
      showToastFromError({
        errorTitle: 'Ocorreu um erro!',
        errorMessage: data.taxSimulatorMessage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  return (
    <ProductCard barColor="product.fundos">
      <VStack space={Spacing['stack.xxxs']}>
        <CardTitle title={fundInfo.fundNameNormalized} />
        <InfoItem label="Resgate Pendente" value={fundInfo.pendingRedemptionValueNormalized} />
        <InfoItem label="Total Bruto" value={fundInfo.grossTotalValueNormalized} />
        <VStack space={Spacing['stack.quarck']} w="full">
          <UI.Text color="neutral.black" variant="semibold">
            Simulador
          </UI.Text>

          <InputMoney
            id="funds-redemption-input"
            name="redemptionValue"
            control={control}
            handleFocus={handleInputFocus}
            handleBlur={handleInputBlur}
            handleSubmit={handleSubmit}
            handleClear={handleInputClear}
            currentValue={valueAppliedFormatted}
            hasFocus={isFocused}
            minValue={fundInfo.minimumTransaction}
            isDisabled={totalValueChecked}
            customInputProps={{
              ...{ ...(isFocused && hasError && { error: hasError }) },
            }}
          />
        </VStack>
        <CustomItem
          label="Resgate Total"
          value={
            <UI.Switch
              size="sm"
              onToggle={() => handleToggleTotalValue()}
              isChecked={totalValueChecked}
            />
          }
        />
        <HighlightedInfos
          infos={[
            { label: 'IR Previsto', value: data?.irValueNormalized, loading },
            { label: 'IOF Previsto', value: data?.iofValueNormalized, loading },
            { label: 'Liquido Previsto', value: data?.liquidityValueNormalized, loading },
          ]}
        />
        <FundRules
          agreementChecked={agreementChecked}
          agreementDisabled={totalValueChecked}
          onCheckAgreement={setAgreementChecked}
          cutOffTime={fundInfo.cutoffTimeNormalized}
          minimumBalanceForPermanence={fundInfo.minimumBalanceNormalized}
          minimumMovementAmount={fundInfo.minimumTransactionNormalized}
          quotationAndSettlement={fundInfo.quotationAndLiquidationNormalized}
          earlyRedemptionDescription={fundInfo.earlyRedemptionDescriptionNormalized}
          quotationDateFormatted={fundInfo.quotationDateFormatted}
        />
        <UI.Button w="full" onPress={handleButtonPress} isDisabled={!agreementChecked || loading}>
          Continuar
        </UI.Button>
      </VStack>
    </ProductCard>
  );
};
